import type { AIRelayState } from '@aibi/ai-core';
import { createAICoreAssistant, type AICoreAssistantOptions } from './ai-core-assistant';
import type { AIRelayApi } from './ai-relay-api';
import type { AssistantConnection, AssistantPort } from './assistant-port';
import { createServerAIProvider } from './server-ai-provider';

export interface ServerBackedAssistantOptions extends Omit<AICoreAssistantOptions, 'provider'> {
  readonly relayApi: AIRelayApi;
}

const CONNECTION_BY_STATE: Readonly<Record<AIRelayState, AssistantConnection>> = {
  unconfigured: 'unverified',
  configured: 'unverified',
  ok: 'connected',
  failing: 'failing',
};

/**
 * Asistente que usa el proveedor de IA del servidor (n8n o Azure AI Foundry) si el servidor lo tiene
 * habilitado y, si no (o si el servidor no responde), el interprete local de reglas. En
 * ambos casos el pipeline del AI Core y la confirmacion explicita son los mismos.
 *
 * El estado se consulta de forma perezosa (al suscribirse o en la primera pregunta) y
 * nunca se muestra `connected` sin una llamada real exitosa.
 *
 * @example
 * const assistant = createServerBackedAssistant({ relayApi, modelCatalog, visualizations, isRenderable });
 */
export function createServerBackedAssistant(options: ServerBackedAssistantOptions): AssistantPort {
  const { relayApi, ...base } = options;
  const local = createAICoreAssistant(base);
  let remote: AssistantPort | null = null;
  let connection: AssistantConnection = 'local';
  let check: Promise<void> | null = null;
  const listeners = new Set<() => void>();

  const notify = (): void => {
    for (const listener of listeners) listener();
  };
  const setConnection = (next: AssistantConnection): void => {
    if (next === connection) return;
    connection = next;
    notify();
  };

  const ensureStatus = (): Promise<void> => {
    check ??= relayApi.status().then((status) => {
      if (!status.ok || status.value.provider === 'unconfigured') return;
      remote = createAICoreAssistant({
        ...base,
        provider: createServerAIProvider({
          api: relayApi,
          kind: status.value.provider,
          onOutcome: (outcome) => {
            setConnection(outcome === 'ok' ? 'connected' : 'failing');
          },
        }),
      });
      setConnection(CONNECTION_BY_STATE[status.value.state]);
    });
    return check;
  };

  const active = (): AssistantPort => remote ?? local;

  return {
    available: true,
    get providerName() {
      return active().providerName;
    },
    get connection() {
      return connection;
    },
    subscribe(listener) {
      listeners.add(listener);
      void ensureStatus();
      return () => {
        listeners.delete(listener);
      };
    },
    async ask(request, signal) {
      await ensureStatus();
      return active().ask(request, signal);
    },
  };
}
